import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { JornadaProvider } from '@/contexts/JornadaContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import '@/App.css';

// Error Boundary para capturar erros de renderização e evitar tela branca
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary capturou:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-800 mb-2">Ocorreu um erro inesperado</h2>
            <p className="text-gray-500 text-sm mb-4">Algo deu errado. Clique abaixo para voltar ao CRM.</p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/dashboard'; }}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              data-testid="error-boundary-reset"
            >
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Componente de rota protegida
const ProtectedRoute = ({ children }) => {
  const { user, loading, senhaExpirada } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (senhaExpirada) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Importar páginas
import NovaVenda from '@/pages/NovaVenda';
import Vendas from '@/pages/Vendas';
import DetalhesVenda from '@/pages/DetalhesVenda';
import EditarVenda from '@/pages/EditarVenda';
import Chat from '@/pages/Chat';
import ChatIA from '@/pages/ChatIA';
import Suporte from '@/pages/Suporte';
import Jornada from '@/pages/Jornada';
import Usuarios from '@/pages/Usuarios';
import Empresas from '@/pages/Empresas';
import Produtos from '@/pages/Produtos';
import BannerConfig from '@/pages/BannerConfig';
import AparenciaLogin from '@/pages/AparenciaLogin';
import RemanejarClaro from '@/pages/RemanejarClaro';
import Mailing from '@/pages/Mailing';
import Sales from '@/pages/Sales';
import Equipes from '@/pages/Equipes';
import Sedes from '@/pages/Sedes';
import JornadaControle from '@/pages/JornadaControle';
import Valores from '@/pages/Valores';
import ConfiguracoesVendas from '@/pages/ConfiguracoesVendas';
import RankingTV from '@/pages/RankingTV';
import RankingPresencial from '@/pages/RankingPresencial';
import RankingHomeOffice from '@/pages/RankingHomeOffice';
import Seguranca from '@/pages/Seguranca';
import GoogleSheets from '@/pages/GoogleSheets';
import MaterialApoio from '@/pages/MaterialApoio';
import NaoPerturbe from '@/pages/NaoPerturbe';
import Viabilidade from '@/pages/Viabilidade';
import DataCenter from '@/pages/DataCenter';
import Backups from '@/pages/Backups';
import Admin2FA from '@/pages/Admin2FA';
import Cancelamentos from '@/pages/Cancelamentos';
import Movel from '@/pages/Movel';
import BackofficeClaro from '@/pages/BackofficeClaro';
import MetasVendas from '@/pages/MetasVendas';

function AppRoutes() {
  const { user, senhaExpirada } = useAuth();

  return (
    <Routes>
      {/* Rota pública */}
      <Route
        path="/login"
        element={user && !senhaExpirada ? <Navigate to="/dashboard" replace /> : <Login />}
      />

      {/* Rotas protegidas */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendas/nova"
        element={
          <ProtectedRoute>
            <NovaVenda />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendas"
        element={
          <ProtectedRoute>
            <Vendas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendas/:id"
        element={
          <ProtectedRoute>
            <DetalhesVenda />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendas/:id/editar"
        element={
          <ProtectedRoute>
            <EditarVenda />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-ia"
        element={
          <ProtectedRoute>
            <ChatIA />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suporte"
        element={
          <ProtectedRoute>
            <Suporte />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jornada"
        element={
          <ProtectedRoute>
            <Jornada />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jornada-controle"
        element={
          <ProtectedRoute>
            <JornadaControle />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/usuarios"
        element={
          <ProtectedRoute>
            <Usuarios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/empresas"
        element={
          <ProtectedRoute>
            <Empresas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/produtos"
        element={
          <ProtectedRoute>
            <Produtos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracoes/banner"
        element={
          <ProtectedRoute>
            <BannerConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracoes/imagem-login"
        element={
          <ProtectedRoute>
            <AparenciaLogin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/remanejar-claro"
        element={
          <ProtectedRoute>
            <RemanejarClaro />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mailing"
        element={
          <ProtectedRoute>
            <Mailing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/equipes"
        element={
          <ProtectedRoute>
            <Equipes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/sedes"
        element={
          <ProtectedRoute>
            <Sedes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/valores"
        element={
          <ProtectedRoute>
            <Valores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/painel/config-vendas"
        element={
          <ProtectedRoute>
            <ConfiguracoesVendas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seguranca"
        element={
          <ProtectedRoute>
            <Seguranca />
          </ProtectedRoute>
        }
      />
      <Route
        path="/google-sheets"
        element={
          <ProtectedRoute>
            <GoogleSheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/material-apoio"
        element={
          <ProtectedRoute>
            <MaterialApoio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nao-perturbe"
        element={
          <ProtectedRoute>
            <NaoPerturbe />
          </ProtectedRoute>
        }
      />
      <Route
        path="/viabilidade"
        element={
          <ProtectedRoute>
            <Viabilidade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-center"
        element={
          <ProtectedRoute>
            <DataCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cancelamentos"
        element={
          <ProtectedRoute>
            <Cancelamentos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/movel"
        element={
          <ProtectedRoute>
            <Movel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/backups"
        element={
          <ProtectedRoute>
            <Backups />
          </ProtectedRoute>
        }
      />

      {/* Rankings Públicos (botão de troca só para ADMIN logado) */}
      <Route path="/ranking-tv" element={<RankingTV />} />
      <Route path="/ranking-presencial" element={<RankingPresencial />} />
      <Route path="/ranking-homeoffice" element={<RankingHomeOffice />} />
      {/* Admin 2FA - Página externa (login próprio) */}
      <Route path="/admin/2fa" element={<Admin2FA />} />
      <Route path="/backoffice-claro" element={<BackofficeClaro />} />
      <Route
        path="/metas"
        element={
          <ProtectedRoute>
            <MetasVendas />
          </ProtectedRoute>
        }
      />

      {/* Redirecionar para dashboard por padrão */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <JornadaProvider>
              <AppRoutes />
            </JornadaProvider>
            <Toaster />
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
